<template>
  <el-dialog v-model="visible" title="个人中心" width="600px" top="8vh" destroy-on-close @open="loadProfile" @closed="handleClosed">
    <el-form :model="form" label-width="100px" v-if="loaded">
      <el-form-item label="头像">
        <div class="avatar-upload">
          <el-avatar :size="80" :src="avatarUrl" class="avatar-preview" />
          <div class="avatar-actions">
            <el-button size="small" @click="triggerUpload">更换头像</el-button>
            <input ref="fileInput" type="file" accept="image/*" style="display:none" @change="handleFileChange" />
            <span class="avatar-hint">支持 JPG/PNG，建议 1:1</span>
          </div>
        </div>
      </el-form-item>

      <el-form-item label="用户名">
        <el-input v-model="form.username" placeholder="请输入用户名" />
      </el-form-item>

      <el-form-item label="姓名">
        <el-input v-model="form.real_name" placeholder="请输入姓名" />
      </el-form-item>

      <el-form-item label="手机号">
        <el-input v-model="form.phone" placeholder="请输入手机号" />
      </el-form-item>

      <el-form-item label="邮箱">
        <el-input v-model="form.email" placeholder="请输入邮箱" />
      </el-form-item>

      <el-form-item label="身份证">
        <el-input v-model="form.id_card" placeholder="请输入身份证号" />
      </el-form-item>

      <el-form-item label="职位">
        <el-input v-model="form.position" placeholder="请输入职位" />
      </el-form-item>

      <el-form-item label="权限名称">
        <el-input :model-value="form.role_name" disabled />
      </el-form-item>

      <el-divider>修改密码</el-divider>

      <el-form-item label="原密码">
        <el-input v-model="passwordForm.oldPassword" type="password" show-password placeholder="请输入原密码" />
      </el-form-item>

      <el-form-item label="新密码">
        <el-input v-model="passwordForm.newPassword" type="password" show-password placeholder="请输入新密码（至少6位）" />
      </el-form-item>
    </el-form>

    <template #footer>
      <el-button @click="visible = false">取消</el-button>
      <el-button type="primary" @click="handleSave" :loading="saving">保存信息</el-button>
      <el-button type="warning" @click="handleChangePassword" :loading="changingPwd" v-if="passwordForm.oldPassword && passwordForm.newPassword">修改密码</el-button>
    </template>
  </el-dialog>
</template>

<script>
import { ref, reactive, computed } from 'vue'
import { ElMessage } from 'element-plus'
import { getProfile, updateProfile, changePassword, uploadAvatar } from '@/api/profileApi'

export default {
  name: 'ProfileDialog',
  props: {
    modelValue: Boolean
  },
  emits: ['update:modelValue', 'profile-updated'],
  setup(props, { emit }) {
    const visible = computed({
      get: () => props.modelValue,
      set: (val) => emit('update:modelValue', val)
    })

    const loaded = ref(false)
    const saving = ref(false)
    const changingPwd = ref(false)
    const uploading = ref(false)
    const fileInput = ref(null)
    const avatarUrl = ref('')
    const currentAvatar = ref('')

    const form = reactive({
      username: '',
      real_name: '',
      phone: '',
      email: '',
      id_card: '',
      position: '',
      role_name: ''
    })

    const passwordForm = reactive({
      oldPassword: '',
      newPassword: ''
    })

    const loadProfile = async () => {
      try {
        const res = await getProfile()
        if (res.code === 200) {
          const data = res.data
          form.username = data.username || ''
          form.real_name = data.real_name || ''
          form.phone = data.phone || ''
          form.email = data.email || ''
          form.id_card = data.id_card || ''
          form.position = data.position || ''
          form.role_name = data.role_name || ''
          avatarUrl.value = data.avatar || ''
          currentAvatar.value = data.avatar || ''
          loaded.value = true
        }
      } catch (e) {
        ElMessage.error('获取用户信息失败')
      }
    }

    const handleClosed = () => {
      loaded.value = false
      passwordForm.oldPassword = ''
      passwordForm.newPassword = ''
    }

    const triggerUpload = () => {
      fileInput.value?.click()
    }

    const handleFileChange = async (e) => {
      const file = e.target.files?.[0]
      if (!file) return
      if (file.size > 5 * 1024 * 1024) {
        ElMessage.warning('图片不能超过5MB')
        return
      }
      uploading.value = true
      try {
        const fd = new FormData()
        fd.append('file', file)
        const res = await uploadAvatar(fd)
        if (res.code === 200) {
          avatarUrl.value = res.data.avatar
          ElMessage.success('头像上传成功')
          emit('profile-updated')
        } else {
          ElMessage.error(res.message || '上传失败')
        }
      } catch (e) {
        ElMessage.error('上传失败')
      } finally {
        uploading.value = false
        e.target.value = ''
      }
    }

    const handleSave = async () => {
      saving.value = true
      try {
        const res = await updateProfile({
          username: form.username,
          real_name: form.real_name,
          phone: form.phone,
          email: form.email,
          id_card: form.id_card,
          position: form.position
        })
        if (res.code === 200) {
          ElMessage.success('保存成功')
          emit('profile-updated')

          const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
          userInfo.real_name = form.real_name
          userInfo.phone = form.phone
          userInfo.username = form.username
          userInfo.avatar = avatarUrl.value
          localStorage.setItem('userInfo', JSON.stringify(userInfo))
        } else {
          ElMessage.error(res.message || '保存失败')
        }
      } catch (e) {
        ElMessage.error('保存失败')
      } finally {
        saving.value = false
      }
    }

    const handleChangePassword = async () => {
      if (!passwordForm.oldPassword || !passwordForm.newPassword) {
        ElMessage.warning('请填写原密码和新密码')
        return
      }
      if (passwordForm.newPassword.length < 6) {
        ElMessage.warning('新密码不能少于6位')
        return
      }
      changingPwd.value = true
      try {
        const res = await changePassword({
          oldPassword: passwordForm.oldPassword,
          newPassword: passwordForm.newPassword
        })
        if (res.code === 200) {
          ElMessage.success('密码修改成功，下次登录请使用新密码')
          passwordForm.oldPassword = ''
          passwordForm.newPassword = ''
        } else {
          ElMessage.error(res.message || '密码修改失败')
        }
      } catch (e) {
        ElMessage.error('密码修改失败')
      } finally {
        changingPwd.value = false
      }
    }

    return {
      visible, loaded, saving, changingPwd, uploading,
      fileInput, avatarUrl, form, passwordForm,
      loadProfile, handleClosed, triggerUpload,
      handleFileChange, handleSave, handleChangePassword
    }
  }
}
</script>

<style scoped>
.avatar-upload {
  display: flex;
  align-items: center;
  gap: 16px;
}
.avatar-preview {
  flex-shrink: 0;
}
.avatar-actions {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.avatar-hint {
  font-size: 12px;
  color: #999;
}
</style>
